import type { FC, ReactNode } from 'react'

import { useCallback, useEffect, useRef, useState } from 'react'
import dynamic from 'next/dynamic'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { CtaLink } from '@/components/generals/cta-link'
import { StatusLabel } from '@/components/generals/status-label'
import { HeroSceneLite } from '@/components/landing/hero-scene-lite'

import { brand, hero } from '@/constants/content'

// The 3D scene is a separate download that only large landscape screens ever request.
const HeroScene3D = dynamic(() => import('@/components/landing/hero-scene-3d'), { ssr: false })

const CINEMATIC_QUERY = '(min-width: 1024px) and (orientation: landscape) and (prefers-reduced-motion: no-preference)'
const READY_TIMEOUT_MS = 3500
const LATE_INTRO_MS = 1500

type HeroMode = 'pending' | '3d' | 'lite'

const supportsWebGL = (): boolean => {
    try {
        const canvas = document.createElement('canvas')
        return Boolean(canvas.getContext('webgl2') ?? canvas.getContext('webgl'))
    }
    catch {
        return false
    }
}

const prefersLightweight = (): boolean => {
    const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection
    return Boolean(connection?.saveData)
}

const HeroSection: FC = (): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const mountedAt = useRef(0)
    const [mode, setMode] = useState<HeroMode>('pending')
    const [load3D, setLoad3D] = useState(false)
    const [inView, setInView] = useState(true)
    const [skipIntro, setSkipIntro] = useState(false)

    useEffect(() => {
        mountedAt.current = performance.now()

        const cinematic = window.matchMedia(CINEMATIC_QUERY).matches

        if (!cinematic || !supportsWebGL() || prefersLightweight()) {
            setMode('lite')
            return
        }

        setLoad3D(true)

        const timer = window.setTimeout(() => {
            setMode(current => (current === 'pending' ? 'lite' : current))
        }, READY_TIMEOUT_MS)

        return () => window.clearTimeout(timer)
    }, [])

    useEffect(() => {
        const element = sectionRef.current
        if (!element) return

        const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { threshold: 0.05 })
        observer.observe(element)

        return () => observer.disconnect()
    }, [])

    useEffect(() => {
        const handleVisibility = (): void => setInView(document.visibilityState === 'visible')
        document.addEventListener('visibilitychange', handleVisibility)

        return () => document.removeEventListener('visibilitychange', handleVisibility)
    }, [])

    const handleReady = useCallback(() => {
        if (performance.now() - mountedAt.current > LATE_INTRO_MS) setSkipIntro(true)
        setMode(current => (current === 'pending' ? '3d' : current))
    }, [])

    return (
        <section
            ref={sectionRef}
            id='hero'
            aria-labelledby='hero-heading'
            data-hero-mode={mode}
            className='relative isolate overflow-hidden'
        >
            <div aria-hidden className='absolute inset-0 -z-20 bg-[radial-gradient(60%_55%_at_78%_45%,rgb(255_138_61/0.12),transparent_70%),radial-gradient(80%_70%_at_15%_0%,rgb(49_82_122/0.5),transparent_70%)] bg-navy-950' />

            {load3D && mode !== 'lite' && (
                <div
                    aria-hidden
                    className={cn(
                        'hero-3d absolute inset-0 -z-10 transition-opacity duration-1000 ease-out',
                        mode === '3d' ? 'opacity-100' : 'opacity-0'
                    )}
                >
                    <HeroScene3D active={inView} skipIntro={skipIntro} onReady={handleReady} />
                    <div className='absolute inset-0 bg-[linear-gradient(90deg,#08131F_0%,rgb(8_19_31/0.88)_28%,rgb(8_19_31/0.2)_55%,transparent_70%)]' />
                    <div className='absolute inset-x-0 bottom-0 h-40 bg-linear-to-t from-navy to-transparent' />
                </div>
            )}

            <div className='container-site grid min-h-[100svh] items-center gap-10 pt-[calc(var(--header-height)+2rem)] pb-24 lg:grid-cols-[1.05fr_0.95fr] lg:gap-6 lg:pt-(--header-height) lg:pb-16'>
                <div className='relative z-10 max-w-2xl animate-in fade-in slide-in-from-bottom-2 duration-500'>
                    <span className='inline-flex rounded-full bg-orange px-3.5 py-1.5 text-xs font-bold tracking-[0.12em] text-navy'>
                        {hero.label}
                    </span>

                    <h1
                        id='hero-heading'
                        className='mt-6 text-[2.6rem] leading-[1.04] font-bold tracking-[-0.03em] text-soft-white sm:text-6xl xl:text-7xl'
                    >
                        {hero.headingLead}{' '}
                        <span className='text-orange'>
                            {hero.headingEmphasis}
                        </span>
                    </h1>

                    <p className='mt-6 max-w-xl text-lg leading-relaxed text-cool-grey-light sm:text-xl'>
                        {hero.sentence}
                    </p>

                    <div className='mt-9 flex flex-col gap-3 sm:flex-row'>
                        <CtaLink href='#play-to-pitch'>
                            {hero.primaryCta}
                        </CtaLink>

                        <CtaLink href='#contact' tone='secondary'>
                            {hero.secondaryCta}
                        </CtaLink>
                    </div>

                    <p className='mt-8 text-sm font-bold tracking-[0.2em] text-orange'>
                        {brand.phrase}
                    </p>
                </div>

                <HeroSceneLite className='transition-opacity duration-500' />
            </div>

            {mode === '3d' && (
                <StatusLabel status='concept' className='hero-3d absolute right-6 bottom-6' />
            )}

            <a
                href='#concept'
                className='absolute bottom-5 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-1 text-xs font-medium text-cool-grey-light transition-colors hover:text-soft-white sm:flex'
            >
                {hero.scrollCue}
                <ChevronDown className='size-4 motion-safe:animate-bounce' aria-hidden />
            </a>
        </section>
    )
}

export default HeroSection
